import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';
import { LuBot } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { StudioStatsV2 } from '../../entities/analytics-v2';
import { getProviderCapabilities } from '../../entities/provider';
import { getStudioStatsV2 } from '../../shared/api/dashboard';
import { useAIChat } from '../../shared/contexts/AIChat.context';
import { useSetDashboardHeader } from '../../shared/contexts/DashboardHeader.context';
import { useDateRange } from '../../shared/contexts/DateRange.provider';
import { useProjects } from '../../shared/contexts/Projects.provider';
import { useConnectedProviders } from '../../shared/hooks/use-connected-providers';
import { formatRiskFlags } from '../../shared/utils/risk-flag-labels';
import { clamp, fmtDays, fmtDaysFromHours, fmtPct, safeNum, trendArrow } from '../../shared/utils/stat-helpers';
import PendingInvitations from '../../widgets/standalone/PendingInvitations';
import {
  InfoTip,
  KpiCard,
  MiniBarChart,
  OtherProjectsCard,
  PsiBadge,
  PsiGauge,
  StatEmpty,
  StatError,
  StatLoading,
} from '../../widgets/stats';
import { ProviderBadge } from '../../widgets/ui/ProviderBadge';
import { StatInsightsPanel } from './StatAIPanel';

export function StudioChartPage() {
  const navigate = useNavigate();
  const dateRangeCtx = useDateRange();
  const projectsCtx = useProjects();
  const setDashHeader = useSetDashboardHeader();
  const { openChat } = useAIChat();
  const { isConnected, isLoading: connLoading } = useConnectedProviders();
  const [studioStats, setStudioStats] = useState<StudioStatsV2 | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (connLoading) return;
    if (!isConnected.jira) {
      setLoading(false);
      return;
    }
    if (!dateRangeCtx.from || !dateRangeCtx.to) return;
    const fetchStudioStats = async () => {
      try {
        const { stat } = await getStudioStatsV2({ from: dateRangeCtx.from!, to: dateRangeCtx.to! });
        setStudioStats(stat);
      } catch (err) {
        console.error('Failed to fetch studio stats:', err);
        setError('Failed to load studio stats.');
      } finally {
        setLoading(false);
      }
    };

    fetchStudioStats();
  }, [dateRangeCtx, connLoading, isConnected.jira]);

  const goProjects = (filter?: 'risk') => {
    if (filter === 'risk') navigate('/dashboard/projects?filter=risk');
    else navigate('/dashboard/projects');
  };

  const summary = studioStats?.summary;
  const delta = studioStats?.trend?.delta;

  const psi = safeNum((studioStats as any)?.psi, 0);

  const throughput = safeNum(summary?.throughputAccuracyPercent);
  const reopenRate = safeNum(summary?.reopenRatePercent);
  const avgCycleHours = safeNum(summary?.avgCycleTimeHours);

  const created = safeNum(summary?.createdCount);
  const completed = safeNum(summary?.completedCount);
  const bugRatio = safeNum(summary?.bugRatioPercent);
  const flowEff = safeNum(summary?.flowEfficiencyPercent);

  const aThroughput = trendArrow(safeNum(delta?.throughputAccuracyPercent), false);
  const aReopen = trendArrow(safeNum(delta?.reopenRatePercent), true);
  const aCycle = trendArrow(safeNum(delta?.avgCycleTimeHours), true);

  const dailyOption = useMemo(() => {
    const series = studioStats?.series ?? [];
    const days = series.map((s: any) => {
      const d = s.day instanceof Date ? s.day : new Date(s.day);
      return isNaN(d.getTime()) ? String(s.day) : d.toLocaleDateString();
    });

    const createdS = series.map((s: any) => safeNum(s.createdCount));
    const completedS = series.map((s: any) => safeNum(s.completedCount));
    const reopenS = series.map((s: any) => safeNum(s.reopenEvents));

    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0, left: 'center' },
      grid: { left: 20, right: 16, top: 18, bottom: 44, containLabel: true },
      xAxis: { type: 'category', data: days, axisLabel: { interval: 'auto' } },
      yAxis: { type: 'value' },
      series: [
        { name: 'Created', type: 'line', data: createdS, smooth: true },
        { name: 'Completed', type: 'line', data: completedS, smooth: true },
        { name: 'Reopens', type: 'bar', data: reopenS, barWidth: 10 },
      ],
    };
  }, [studioStats]);

  const caps = useMemo(() => {
    const cycleDays = avgCycleHours / 24;
    return {
      throughput: 100,
      reopen: 40,
      cycleDays: Math.max(7, cycleDays * 1.6 || 7),
    };
  }, [avgCycleHours]);

  useEffect(() => {
    setDashHeader({
      component: 'studio',
      title: (
        <span className="inline-flex items-center gap-2">
          Studio overview
          {isConnected.jira && <ProviderBadge provider="jira" />}
        </span>
      ),
      subtitle: studioStats ? (
        <>
          Range:{' '}
          <span className="font-medium">
            {new Date(studioStats.aiInsightPayload?.range?.from ?? Date.now()).toLocaleDateString()} —{' '}
            {new Date(studioStats.aiInsightPayload?.range?.to ?? Date.now()).toLocaleDateString()}
          </span>
        </>
      ) : undefined,
      actions: (
        <div className="flex items-center gap-2">
          <button
            className="btn btn-primary btn-sm shadow-glow gap-1.5"
            onClick={() => {
              openChat({
                suggestedPrompts: [
                  'What is the main risk across all projects right now?',
                  'Which projects need attention first?',
                  ...(reopenRate > 10 ? ['Why is the studio reopen rate high?'] : []),
                  ...(avgCycleHours / 24 > 7 ? ['What is causing the long cycle times?'] : []),
                ],
                insightText: studioStats?.aiInsightPayload?.notes?.riskFlags?.length
                  ? formatRiskFlags(studioStats.aiInsightPayload.notes.riskFlags)
                  : undefined,
                analyticsContext: {
                  scope: 'studio',
                  dateRange:
                    dateRangeCtx?.from && dateRangeCtx?.to
                      ? {
                          from: dateRangeCtx.from.toISOString().split('T')[0],
                          to: dateRangeCtx.to.toISOString().split('T')[0],
                        }
                      : undefined,
                },
              });
            }}
          >
            <LuBot size={14} />
            Ask mFlow
          </button>
          {/*<button className="btn btn-outline btn-sm" onClick={() => navigate('/dashboard/projects')}>*/}
          {/*  Open projects →*/}
          {/*</button>*/}
        </div>
      ),
    });
    return () => setDashHeader({});
  }, [studioStats, setDashHeader, navigate, isConnected.jira]);

  const otherProjects = (projectsCtx ?? []).filter(
    (p: any) => p.provider && !getProviderCapabilities(p.provider).supportsStudioOverview,
  );

  if (connLoading || loading) return <StatLoading />;

  if (!isConnected.jira) {
    return (
      <div className="min-h-screen bg-base-200">
        <div className="p-4 space-y-4">
          <PendingInvitations />
          {otherProjects.length > 0 ? (
            <OtherProjectsCard projects={otherProjects} />
          ) : (
            <StatEmpty message="Connect Jira, Trello, or start a standalone project to see your dashboard." />
          )}
        </div>
      </div>
    );
  }

  if (error) return <StatError message={error} />;

  if (!studioStats) {
    return (
      <div className="min-h-screen bg-base-200">
        <div className="p-4 space-y-4">
          <PendingInvitations />
          {otherProjects.length > 0 ? (
            <OtherProjectsCard projects={otherProjects} />
          ) : (
            <StatEmpty message="No data available." />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        <PendingInvitations />
        {otherProjects.length > 0 && <OtherProjectsCard projects={otherProjects} />}

        <StatInsightsPanel
          riskFlags={studioStats.aiInsightPayload?.notes?.riskFlags}
          sentiment={(studioStats as any).aiSummary?.sentiment || 'neutral'}
          riskLevel={(studioStats as any).aiSummary?.riskLevel || 'medium'}
          statId={(studioStats as any)._id}
        />

        {/* Top KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* PSI */}
          <KpiCard
            label={
              <>
                <span className="flex items-center gap-1">
                  PSI
                  <InfoTip
                    text={
                      'Project Stability Index (0–100)\n\nCombines:\n- Throughput (completed / created)\n- Quality (reopen rate)\n- Flow (cycle time + efficiency)\n\nHigher = more stable delivery.'
                    }
                  />
                </span>
                <PsiBadge psi={psi} />
              </>
            }
            footer={`Created ${created} · Completed ${completed}`}
          >
            <div className="grid grid-cols-2 gap-2 mt-2 items-center">
              <div className="text-3xl font-semibold">{psi}</div>
              <PsiGauge value={psi} />
            </div>
          </KpiCard>

          {/* Throughput */}
          <KpiCard
            label={
              <>
                Throughput accuracy
                <InfoTip
                  text={
                    'Throughput accuracy = completedCount / createdCount\n\nShows how much incoming work is actually finished in the selected range.\nGood for studios without due dates.'
                  }
                />
              </>
            }
            footer={`Created ${created} · Completed ${completed}`}
          >
            <div className="flex items-end gap-2">
              <div className="text-3xl font-semibold">{fmtPct(throughput)}</div>
              <div className={`text-sm ${aThroughput.cls}`}>
                {aThroughput.s} {fmtPct(safeNum(delta?.throughputAccuracyPercent))}
              </div>
            </div>
            <MiniBarChart label="Throughput" value={clamp(throughput, 0, 100)} max={caps.throughput} suffix="%" />
            <div className="text-xs mt-2 opacity-70">
              Bug ratio:{' '}
              <span
                className={
                  bugRatio > 30 ? 'text-error font-medium' : bugRatio > 20 ? 'text-warning font-medium' : 'font-medium'
                }
              >
                {bugRatio > 0 ? fmtPct(bugRatio) : '—'}
              </span>
            </div>
          </KpiCard>

          {/* Reopen */}
          <KpiCard
            label={
              <>
                Reopen rate
                <InfoTip
                  text={
                    'Reopen rate = reopenEvents / completedCount\n\nHigh reopen rate usually means quality issues or unclear requirements.\nLower is better.'
                  }
                />
              </>
            }
            footer={`Reopen events ${safeNum(summary?.reopenEvents)}`}
          >
            <div className="flex items-end gap-2">
              <div className="text-3xl font-semibold">{fmtPct(reopenRate)}</div>
              <div className={`text-sm ${aReopen.cls}`}>
                {aReopen.s} {fmtPct(safeNum(delta?.reopenRatePercent))}
              </div>
            </div>
            <MiniBarChart label="Reopen" value={clamp(reopenRate, 0, caps.reopen)} max={caps.reopen} suffix="%" />
          </KpiCard>

          {/* Cycle & flow */}
          <KpiCard
            label={
              <>
                Cycle & flow
                <InfoTip
                  text={
                    'Avg cycle time = average (createdAt → doneAt). Lower is better.\n\nFlow efficiency = time in active statuses / total cycle time.\nIndustry avg: 15–40%. < 15% = lots of waiting. > 60% = very healthy.'
                  }
                />
              </>
            }
          >
            <div className="text-sm mt-1">
              Cycle: <span className="font-medium">{fmtDays(avgCycleHours / 24)}</span>{' '}
              <span className={`ml-2 ${aCycle.cls}`}>
                {aCycle.s} {fmtDays(safeNum(delta?.avgCycleTimeHours) / 24)}
              </span>
            </div>
            <MiniBarChart label="Cycle" value={avgCycleHours / 24} max={caps.cycleDays} suffix="d" height="48px" />
            <div className="text-sm mt-2">
              Flow efficiency:{' '}
              <span
                className={
                  flowEff > 60
                    ? 'text-success font-medium'
                    : flowEff > 0 && flowEff < 15
                      ? 'text-warning font-medium'
                      : 'font-medium'
                }
              >
                {flowEff > 0 ? fmtPct(flowEff) : '—'}
              </span>
            </div>
            {flowEff > 0 && (
              <MiniBarChart label="Flow eff." value={clamp(flowEff, 0, 100)} max={100} suffix="%" height="48px" />
            )}
          </KpiCard>
        </div>

        {/* Main visual block */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Trend */}
          <div className="card bg-base-100 shadow-card lg:col-span-2">
            <div className="card-body p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold flex items-center">
                    Trend (Created vs Completed)
                    <InfoTip
                      text={
                        'Daily time series in the selected range.\n\nCreated: tasks created that day.\nCompleted: tasks moved into Done/Closed/Resolved that day.\nReopens: tasks moved from Done/Closed/Resolved back to active.'
                      }
                    />
                  </div>
                  <div className="text-sm opacity-70">Use it to see activity and stability patterns.</div>
                </div>
                <button className="btn btn-sm btn-outline" onClick={() => goProjects()}>
                  Drill down →
                </button>
              </div>

              <div className="h-[360px] mt-2">
                <ReactECharts option={dailyOption} style={{ height: '100%' }} />
              </div>
            </div>
          </div>

          {/* Side panels */}
          <div className="space-y-3">
            {/* Risk projects */}
            <div className="card bg-base-100 shadow-card">
              <div className="card-body p-4">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold flex items-center">
                    Risk projects
                    <InfoTip
                      text={
                        'Projects sorted by risk score.\n\nRisk uses:\n- low delivery accuracy (weighted by due coverage)\n- high reopen rate\n- high cycle time'
                      }
                    />
                  </div>
                  <button className="btn btn-xs btn-outline" onClick={() => goProjects('risk')}>
                    Open →
                  </button>
                </div>

                {studioStats.topProjectsByRisk?.length ? (
                  <div className="overflow-x-auto mt-2">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th className="text-right">Reopen</th>
                          <th className="text-right">Cycle</th>
                          <th className="text-right">Bug %</th>
                          <th className="text-right">WIP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studioStats.topProjectsByRisk.slice(0, 8).map((p) => (
                          <tr
                            key={p.projectId}
                            className="hover cursor-pointer"
                            onClick={() => navigate(`/dashboard/projects/${p.projectId}`)}
                          >
                            <td className="font-medium">
                              {projectsCtx?.find((pr: any) => pr._id === p.projectId)?.name || p.projectId}
                            </td>
                            <td className="text-right">{fmtPct(safeNum(p.reopenRatePercent))}</td>
                            <td className="text-right">{fmtDaysFromHours(safeNum(p.avgCycleTimeHours))}</td>
                            <td
                              className={`text-right ${safeNum(p.bugRatioPercent) > 30 ? 'text-error' : safeNum(p.bugRatioPercent) > 20 ? 'text-warning' : ''}`}
                            >
                              {p.bugRatioPercent > 0 ? fmtPct(safeNum(p.bugRatioPercent)) : '—'}
                            </td>
                            <td className="text-right">{p.wipSnapshot > 0 ? safeNum(p.wipSnapshot) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm opacity-60 mt-2">No projects in this range.</div>
                )}
              </div>
            </div>

            {/* Top users */}
            <div className="card bg-base-100 shadow-card">
              <div className="card-body p-4">
                <div className="text-lg font-semibold flex items-center">
                  Top contributors
                  <InfoTip
                    text={
                      'Users sorted by completed tasks in this range.\n\nFor each user:\n- completed count\n- reopen rate\n- avg cycle time'
                    }
                  />
                </div>

                {studioStats.topUsersByCompleted?.length ? (
                  <div className="overflow-x-auto mt-2">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th className="text-right">Done</th>
                          <th className="text-right">Reopen</th>
                          <th className="text-right">Cycle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studioStats.topUsersByCompleted.slice(0, 8).map((u: any) => (
                          <tr key={u.userId} className="hover">
                            <td className="font-medium">{u.userName || u.userId}</td>
                            <td className="text-right">{safeNum(u.completedCount)}</td>
                            <td className="text-right">{fmtPct(safeNum(u.reopenRatePercent))}</td>
                            <td className="text-right">{fmtDaysFromHours(safeNum(u.avgCycleTimeHours))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm opacity-60 mt-2">No completed tasks in this range.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="text-xs opacity-60">
          Tip: Studio KPI cards show range totals. Trend compares this range vs the previous equal-length range.
        </div>
      </div>
    </div>
  );
}
